import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import {
  Box,
  CssBaseline,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import { ReactNode, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { PAGES_ROUTES } from '../../../Constants'
import { checkModuleAccess } from '../../../Helpers/Util'
import Header from '../Header'
import { Drawer, DrawerHeader } from './MUI-Drawer-Style'
import './muiDrawer.scss'

export interface MUIDrawerProps {
  children?: ReactNode
  buildConfig?: {
    version: string
    releaseDate: string
  } | null
  drawerData?: any[]
  isPlainLayout?: boolean
}

export default function MUIDrawer({
  children,
  buildConfig,
  drawerData = [],
  isPlainLayout = false,
}: MUIDrawerProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarItems, setSidebarItems] = useState<any>([])

  const [open, setOpen] = useState(!isPlainLayout)
  const [openNav, setOpenNav] = useState<any>([])

  useEffect(() => {
    if (isPlainLayout) setOpen(false)
  }, [isPlainLayout])

  useEffect(() => {
    activateSelectedRoute(drawerData)
  }, [location.pathname, location?.state?.fromPage, drawerData])

  const activateSelectedRoute = (drawerDataArgs: any = []) => {
    const fromPage = location?.state?.fromPage || ''
    const openNavCopy: any = []
    const makeActive = (data: any, parentDetails: any = '') => {
      for (const item of data) {
        if (location.pathname.includes(item.path) || fromPage === item.id) {
          item.active = true
          if (parentDetails) {
            parentDetails.active = true
            openNavCopy.push(parentDetails.id)
          }
          openNavCopy.push(item.id)
        } else {
          item.active = false
        }
        if (item.child?.length) {
          makeActive(item.child, item)
        }
      }
    }
    makeActive(drawerDataArgs)
    setOpenNav(openNavCopy)
    setSidebarItems(drawerData)
  }
  const handleNavigation = (listItem: any) => {
    const hasAccess = checkModuleAccess(listItem?.role)
    const path = hasAccess ? listItem.path : `${PAGES_ROUTES.AccessDenied}`
    const state = !hasAccess ? { state: { fromPage: listItem?.id } } : undefined

    navigate(path, state)
  }
  const handleOnClickItem = (listItemObj: any) => {
    let openNavCopy = [...openNav]
    if (listItemObj.child?.length) {
      if (!open) {
        handleNavigation(listItemObj)
        return
      }
      if (openNavCopy.includes(listItemObj.id)) {
        openNavCopy = openNavCopy.filter((item) => item !== listItemObj.id)
      } else {
        openNavCopy = [listItemObj.id]
      }
      setOpenNav(openNavCopy)
    } else {
      let path = listItemObj.path
      if (!checkModuleAccess(listItemObj?.role)) {
        path = `${PAGES_ROUTES.AccessDenied}`
      }
      path === `${PAGES_ROUTES.AccessDenied}`
        ? navigate(path, { state: { fromPage: listItemObj?.id } })
        : navigate(path)
    }
  }
  const getContentClassName = (open: any, isPlainLayout: any) => {
    if (open && !isPlainLayout) return 'content-open-layout'
    if (open && isPlainLayout) return 'content-open-plain-layout'
    if (!open && isPlainLayout) return 'content-close-plain-layout'
    return 'content-close-layout'
  }
  return (
    <Box>
      <CssBaseline />
      <Header setOpen={setOpen} drawerState={open} />
      <Drawer
        className='left-sidebar'
        variant='permanent'
        open={open}
        isPlainLayout={isPlainLayout}
      >
        <Divider />
        <List
          className={`sidebar-list text-inter ${open ? 'open-sidebar' : ''}`}
          style={{ marginTop: '60px' }}
        >
          {sidebarItems.map((listItemObj: any) => (
            <ListItem
              className='text-inter'
              key={listItemObj.id}
              disablePadding
              sx={{ display: 'block' }}
            >
              <DrawerListItem
                listItemObj={listItemObj}
                openNav={openNav}
                onClickItem={(e: any) => {
                  e.preventDefault()
                  handleOnClickItem(listItemObj)
                }}
                isChild={false}
                open={open}
              />
              {(openNav || []).includes(listItemObj.id) &&
                open &&
                listItemObj.child?.map((childItemObj: any) => (
                  <DrawerListItem
                    key={childItemObj.id}
                    listItemObj={childItemObj}
                    openNav={openNav}
                    onClickItem={(e: any) => {
                      e.preventDefault()
                      handleOnClickItem(childItemObj)
                    }}
                    isChild={true}
                    open={open}
                  />
                ))}
            </ListItem>
          ))}
        </List>
        {open && (
          <div className='sidebar-footer-main'>
            <h6>Application Version : {buildConfig?.version}</h6>
            <p>Last Released on {buildConfig?.releaseDate}</p>
          </div>
        )}
      </Drawer>
      <Box component='main' sx={{ flexGrow: 1, p: 0 }}>
        <Grid
          className={getContentClassName(open, isPlainLayout)}
          container
          spacing={{ xs: 2, md: 3 }}
          columns={{ xs: 12 }}
        >
          <DrawerHeader />
          <Grid item xs={12}>
            {children}
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export interface ListItemPorpsType {
  listItemObj: any
  openNav?: any[]
  onClickItem?: any
  isChild: boolean
  open: boolean
}

export const DrawerListItem = ({
  listItemObj,
  openNav,
  onClickItem,
  isChild,
  open,
}: ListItemPorpsType) => {
  const isOpen = (openNav || []).includes(listItemObj.id)

  let color = ''
  let borderLeft = ''
  let backgroundColor = ''

  if (listItemObj.active) {
    color = isChild ? '#ffff' : '#402080'
    backgroundColor = isChild ? '#402080' : '#ECE9F2'
  }

  if (!isChild) {
    borderLeft = listItemObj.active ? '2px solid #402080' : '2px solid #ffff'
  }

  return (
    <ListItemButton
      sx={{
        minHeight: '52px',
        display: 'flex',
        alignItems: 'center',
        margin: '10px',
        borderRadius: '4px',
        justifyContent: open ? 'initial' : 'center',
        borderBottom: isChild ? '1px solid #ECE9F2' : '',
        color: color,
        borderLeft: borderLeft,
        backgroundColor: backgroundColor,
        ':hover': { backgroundColor: '#ECE9F2', color: 'black' },
        // ':hover': isChild ? { backgroundColor: '#ECE9F2', color: 'black' } : {},
      }}
      className={`text-inter ${listItemObj.active ? 'active' : ''}`}
      onClick={onClickItem}
      data-testid={listItemObj.id}
    >
      {listItemObj.icon && (
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: open ? 1.5 : 0,
            justifyContent: 'center',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {isOpen ? listItemObj.activeIcon : listItemObj.icon}
        </ListItemIcon>
      )}
      {listItemObj.child?.length ? (
        <ListItemText
          primary={
            <div className='d-flex align-items-center justify-content-between'>
              <Typography className='text-inter font-medium'> {listItemObj.lable}</Typography>
              {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </div>
          }
          sx={{ opacity: open ? 1 : 0 }}
        />
      ) : (
        <Link
          to={listItemObj.path}
          state={{ fromPage: listItemObj?.id }}
          style={{
            textDecoration: 'none',
            display: 'contents',
            alignItems: 'center',
            width: '100%',
          }}
          className='table-link'
        >
          <ListItemText
            className='text-inter font-medium'
            primary={listItemObj.lable}
            sx={{ paddingLeft: isChild ? 4 : 0, opacity: open ? 1 : 0 }}
          />
        </Link>
      )}
    </ListItemButton>
  )
}
